import { DefaultLayout } from '@components/DefaultLayout';
import { ROUTES } from 'src/config/routes';
import { AuthRouteProps, RoutesProps } from '@typings/routes';
import { lazy } from 'react';
import { useAuthContext } from '@contexts/auth.context';
import { Navigate, useLocation } from 'react-router-dom';
import { StorageManager } from '@storage/index';
import { User, UserType } from '@models/User';

const SelectModule = lazy(() => import('@pages/SelectModule').then(module => ({ default: module.SelectModule })));
const StudentCalendar = lazy(() =>
	import('@pages/StudentCalendar').then(module => ({ default: module.StudentCalendar })),
);
const TutoringGrades = lazy(() => import('@pages/TutoringGrades').then(module => ({ default: module.TutoringGrades })));
const ReportsPage = lazy(() => import('@pages/ReportsPage').then(module => ({ default: module.ReportsPage })));
const ThrowCognitiveNotes = lazy(() =>
	import('@pages/ThrowCognitiveNotes').then(module => ({ default: module.ThrowCognitiveNotes })),
);

const HomeStudent = lazy(() => import('@pages/HomeStudent').then(module => ({ default: module.HomeStudent })));
const ConsultPerformanceMentoringProgress = lazy(() =>
	import('@pages/ConsultPerformanceMentoringProgress').then(module => ({
		default: module.ConsultPerformanceMentoringProgress,
	})),
);
const HomeTeacher = lazy(() => import('@pages/HomeTeacher').then(module => ({ default: module.HomeTeacher })));
const ParameterizeProblem = lazy(() =>
	import('@pages/ParameterizeProblem').then(module => ({ default: module.ParameterizeProblem })),
);
const ProblemHistory = lazy(() => import('@pages/ProblemHistory').then(module => ({ default: module.ProblemHistory })));
const NoteRelease = lazy(() => import('@pages/NoteRelease').then(module => ({ default: module.NoteRelease })));
const TutorNotes = lazy(() => import('@pages/TutorNotes').then(module => ({ default: module.TutorNotes })));
const Inconsistencies = lazy(() =>
	import('@pages/Inconsistencies').then(module => ({ default: module.Inconsistencies })),
);
const LaunchMorphofunctionalNotes = lazy(() =>
	import('@pages/LaunchMorphofunctionalNotes').then(module => ({ default: module.LaunchMorphofunctionalNotes })),
);
const RegisterEvaluation = lazy(() =>
	import('@pages/RegisterEvaluation').then(module => ({ default: module.RegisterEvaluation })),
);

const GeneralNotes = lazy(() => import('@pages/GeneralNotes').then(module => ({ default: module.GeneralNotes })));
const GeneralNotesMorfo = lazy(() =>
	import('@pages/GeneralNotesMorfo').then(module => ({ default: module.GeneralNotesMorfo })),
);

const TeacherLaunchNotes = lazy(() =>
	import('@pages/TeacherLaunchNotes').then(module => ({ default: module.TeacherLaunchNotes })),
);
const SelectPerlet = lazy(() => import('@pages/SelectPerlet').then(module => ({ default: module.SelectPerlet })));
const Home = lazy(() => import('@pages/Home').then(module => ({ default: module.Home })));
const AccessDenied = lazy(() => import('@pages/AccessDenied').then(module => ({ default: module.AccessDenied })));
const CosultNotes = lazy(() => import('@pages/CosultNotes').then(module => ({ default: module.CosultNotes })));

const AuthRoute = ({
	children,
	hasLayout = false,
	hasHeader = true,
	hasFooter = true,
	headerProps,
	accessLevel,
}: AuthRouteProps) => {
	const storage = new StorageManager();
	const location = useLocation();

	const { isAuth } = useAuthContext();
	const user = storage.getItem<User>('user');
	const path = location.pathname;

	if (!isAuth) {
		return <Navigate to={ROUTES.login} replace />;
	}

	if (user && user.ENTRARCOMO !== accessLevel && accessLevel !== UserType.PROFESSOR) {
		return <Navigate to={ROUTES.accessDenied} replace />;
	}

	if (
		user &&
		!user.IDPERLET &&
		(user.ENTRARCOMO === UserType.PROFESSOR || user.ENTRARCOMO === UserType.ALUNO) &&
		path !== ROUTES.professorSelectPerlet &&
		path !== ROUTES.selectModule
	) {
		if (user.ENTRARCOMO === UserType.PROFESSOR) {
			return <Navigate to={ROUTES.professorSelectPerlet} />;
		} else if (user.ENTRARCOMO === UserType.ALUNO) {
			// Redirecionar o usuário aluno para outro lugar
			return <Navigate to={ROUTES.selectModule} />;
		}
	}

	return hasLayout ? <DefaultLayout {...{ hasHeader, headerProps, hasFooter }}>{children}</DefaultLayout> : children;
};

const commomRoutes: RoutesProps[] = [
	{
		path: ROUTES.home,
		element: <Home />,
		hasLayout: false,
		accessLevel: UserType.COMUM,
	},
	{
		path: ROUTES.accessDenied,
		element: <AccessDenied />,
		hasLayout: false,
		accessLevel: UserType.COMUM,
	},
];

const studentRoutes: RoutesProps[] = [
	{
		path: ROUTES.studentCalendar,
		element: <StudentCalendar />,
		hasLayout: true,
		accessLevel: UserType.ALUNO,
	},
	{
		path: ROUTES.selectModule,
		element: <SelectModule />,
		hasLayout: false,
		accessLevel: UserType.ALUNO,
	},
	{
		path: ROUTES.homeStudent,
		element: <HomeStudent />,
		hasLayout: true,
		accessLevel: UserType.ALUNO,
	},
	{
		path: ROUTES.problemHistory,
		element: <ProblemHistory />,
		hasLayout: true,
		accessLevel: UserType.ALUNO,
	},
	{
		path: ROUTES.noteRelease,
		element: <NoteRelease />,
		hasLayout: true,
		accessLevel: UserType.ALUNO,
	},
	{
		path: ROUTES.consultNotes,
		element: <CosultNotes />,
		hasLayout: true,
		accessLevel: UserType.ALUNO,
	},
];

const teacherRoutes: RoutesProps[] = [
	{
		path: ROUTES.professorSelectPerlet,
		element: <SelectPerlet />,
		hasLayout: false,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.tutoringGrades,
		element: <TutoringGrades />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.TutorNotes,
		element: <TutorNotes />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.generalNotes,
		element: <GeneralNotes />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.generalMorfo,
		element: <GeneralNotesMorfo />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.homeTeacher,
		element: <HomeTeacher />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.teacherLaunchNotes,
		element: <TeacherLaunchNotes />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},

	{
		path: ROUTES.LaunchMorphofunctionalNotes,
		element: <LaunchMorphofunctionalNotes />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.reportsPage,
		element: <ReportsPage />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.inconsistencies,
		element: <Inconsistencies />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.consultPerformanceMentoringProgress,
		element: <ConsultPerformanceMentoringProgress />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.registerEvaluation,
		element: <RegisterEvaluation />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.throwCognitiveNotes,
		element: <ThrowCognitiveNotes />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
	{
		path: ROUTES.parameterizeProblem,
		element: <ParameterizeProblem />,
		hasLayout: true,
		accessLevel: UserType.PROFESSOR,
	},
];

const protectedRoutes: RoutesProps[] = [...commomRoutes, ...studentRoutes, ...teacherRoutes];

export { AuthRoute, protectedRoutes };
